import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  fetchMfps,
  fetchPlanMfps,
  syncPlanMfps,
  type PaginatedMfpResponse,
  type PlanMfpRead,
} from '@/api/mfps'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

/**
 * Fetch available MFPs as dropdown options (infinite scroll, server-side search)
 */
export function useMfpOptions(search?: string, enabled = true) {
  return useInfiniteQuery<PaginatedMfpResponse>({
    queryKey: [...queryKeys.mfps.list(), search ?? ''],
    queryFn: ({ pageParam = 0 }) =>
      fetchMfps({
        search,
        skip: pageParam as number,
      }),
    getNextPageParam: lastPage =>
      lastPage.pagination.has_more
        ? lastPage.pagination.skip + lastPage.pagination.limit
        : undefined,
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

/**
 * Fetch MFPs associated with a plan
 */
export function usePlanMfps(planId: string) {
  return useQuery<PlanMfpRead[]>({
    queryKey: queryKeys.mfps.byPlan(planId),
    queryFn: () => fetchPlanMfps(planId),
    enabled: !!planId,
  })
}

/**
 * Sync (replace) MFP associations for a plan
 */
export function useSyncPlanMfps(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (mfpIds: string[]) => syncPlanMfps(planId, { mfp_ids: mfpIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.mfps.byPlan(planId),
      })
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
      queryClient.invalidateQueries({
        queryKey: queryKeys.mfps.byPlan(planId),
      })
    },
  })
}
